// Rails' per-session CSRF token, off the meta tag the layout renders. Needed by
// anything that talks to the server outside Inertia's own router - fetch calls
// carry no token unless we put one on them.
export function csrfToken() {
  return (
    document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content") ?? ""
  );
}

// Rails rotates the CSRF token whenever the session is reset - signing in, or
// signing in again in another tab. The token lives in a meta tag stamped into
// the document at load, and Inertia never reloads the document, so a tab left
// open across a sign-in carries a token the server will refuse.
//
// That is a page editor's normal life: it is open for an hour while somebody
// writes. So rather than failing the save, refetch the current page, lift the
// fresh token out of it, and try once more.
async function refreshedToken() {
  try {
    const res = await fetch(window.location.href, {
      headers: { Accept: "text/html" },
      credentials: "same-origin",
    });
    if (!res.ok) return null;

    const meta = (await res.text()).match(
      /name="csrf-token"[^>]*content="([^"]+)"/,
    );
    if (!meta) return null;

    document
      .querySelector('meta[name="csrf-token"]')
      ?.setAttribute("content", meta[1]);
    return meta[1];
  } catch {
    return null;
  }
}

// A stale token reads as a 422 carrying Rails' HTML error page, where every
// endpoint we call here answers in JSON. That is the tell worth retrying on -
// a real validation failure is JSON and must be shown, not retried.
function looksLikeStaleToken(res: Response) {
  return (
    res.status === 422 &&
    !(res.headers.get("content-type") ?? "").includes("json")
  );
}

export async function fetchWithCsrf(url: string, init: RequestInit = {}) {
  const send = (token: string) =>
    fetch(url, {
      ...init,
      credentials: "same-origin",
      headers: { ...(init.headers ?? {}), "X-CSRF-Token": token },
    });

  const first = await send(csrfToken());
  if (!looksLikeStaleToken(first)) return first;

  const fresh = await refreshedToken();
  return fresh ? send(fresh) : first;
}
